import React from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import FailBack from './components/Failback'
import { useAppContext } from './hooks/AppContextProvider'
import RoleMiddleware from './middleware/roleMiddleware'
import { ROUTE_PAGE } from './utils/route'

const App = React.lazy(async () => ({
  default: (await import('./App')).default,
}))
const NotFound = React.lazy(async () => ({
  default: (await import('./pages/NotFound')).default,
}))

const publicRoutes: any = [
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/QRVerification')).default,
    })),
    path: '/qr/verify/:tenantDomain/:nftId',
  },
]

const loginRoutes: any = [
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/Login')).default,
    })),
    path: '/login',
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/GoogleCallback')).default,
    })),
    path: '/google-auth/callback',
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/MicrosoftCallback')).default,
    })),
    path: '/microsoft-auth/callback',
  },
]

// Export privateRoutes so sidebar can access route permissions
export const privateRoutes: any = [
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/PrivateData')).default,
    })),
    path: '/user/:tenant/:userTenant/:nftId',
    allowedRoles: ['user', 'tenant', 'masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/PrivateData')).default,
    })),
    path: '/:tenant/:userTenant/:nftId',
    allowedRoles: ['tenant', 'masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/Products')).default,
    })),
    path: '/:tenant',
    allowedRoles: ['tenant', 'masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/ReceivedProducts')).default,
    })),
    path: '/user/:tenant/:userTenant',
    allowedRoles: ['user', 'tenant', 'masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/Products')).default,
    })),
    path: '/:tenant/:userTenant',
    allowedRoles: ['tenant', 'masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/TenantDomainList')).default,
    })),
    path: '/tenant-domains',
    allowedRoles: ['user', 'tenant', 'masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/TenantManagement')).default,
    })),
    path: '/tenant-management',
    allowedRoles: ['masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/ProductManagement')).default,
    })),	
    path: ROUTE_PAGE.PRODUCT_MANAGEMENT,
    allowedRoles: ['tenant', 'masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/PrivateDataDetail')).default,
    })),
    path: `${ROUTE_PAGE.PRODUCT_MANAGEMENT}/:tenant/:id`,
    allowedRoles: ['tenant', 'masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/TenantMaster')).default,
    })),
    path: `${ROUTE_PAGE.PRODUCT_MANAGEMENT}/master`,
    allowedRoles: ['tenant', 'masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/InspectionItemList')).default,
    })),
    path: '/inspection-item-list',
    allowedRoles: ['tenant', 'masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/InspectionItemDetail')).default,
    })),
    path: '/inspection-item-list/:id',
    allowedRoles: ['tenant', 'masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/EmbedReportComponent')).default,
    })),
    path: '/inspection-item-list/:productCode/:nameProperty/view-analytic',
    allowedRoles: ['tenant', 'masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/ApiRequestHistory')).default,
    })),
    path: ROUTE_PAGE.API_REQUEST_HISTORY,
    allowedRoles: ['tenant', 'masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/CustomizeDisplayForProduct')).default,
    })),
    path: ROUTE_PAGE.CUSTOMIZE_DISPLAY_FOR_PRODUCT,
    allowedRoles: ['tenant', 'masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/CustomizeDisplaySetting')).default,
    })),
    path: ROUTE_PAGE.CUSTOMIZE_DISPLAY_SETTING,
    allowedRoles: ['tenant', 'masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/ApiIntegrationGuideline')).default,
    })),
    path: '/api-integration-guideline',
    allowedRoles: ['tenant', 'masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/AnalyticsRequests')).default,
    })),
    path: ROUTE_PAGE.ANALYTICS_REQUESTS,
    allowedRoles: ['masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/AnalyticsRequestDetail')).default,
    })),
    path: ROUTE_PAGE.ANALYTICS_REQUEST_DETAIL,
    allowedRoles: ['masterTenant'],
  },
  {
    component: React.lazy(async () => ({
      default: (await import('./pages/UploadUserTestData')).default,
    })),
    path: ROUTE_PAGE.UPLOAD_USER_TEST_DATA,
    allowedRoles: ['user'],
  },
]

const AppRoutes = () => {
  const { token } = useAppContext()
  const location = useLocation()
  const isNotFound = location.pathname === '/not-found'

  return (
    <>
      <Routes>
        <Route path="/">
          <Route
            index
            element={
              token ? <FailBack component={App} /> : <Navigate to="/login" />
            }
          />
          {publicRoutes.length &&
            publicRoutes.map((route: any, index: number) => {
              const { component: Component, path } = route
              return (
                <Route
                  key={`${index}-public-router`}
                  path={path}
                  element={<FailBack component={Component} />}
                />
              )
            })}
          {loginRoutes.length &&
            loginRoutes.map((route: any, index: number) => {
              const { component: Component, path } = route
              return (
                <Route
                  key={`${index}-login-router`}
                  path={path}
                  element={
                    token ? (
                      <Navigate to="/" />
                    ) : (
                      <FailBack component={Component} />
                    )
                  }
                />
              )
            })}
          {privateRoutes.length &&
            privateRoutes.map((route: any, index: number) => {
              const { component: Component, path, allowedRoles } = route
              return (
                <Route
                  key={`${index}-private-router`}
                  path={path}
                  element={
                    token ? (
                      <RoleMiddleware allowedRoles={allowedRoles}>
                        <FailBack component={Component} />
                      </RoleMiddleware>
                    ) : (
                      <Navigate to="/login" />
                    )
                  }
                />
              )
            })}
        </Route>
        <Route path="/not-found" element={<FailBack component={NotFound} />} />
        <Route path="*" element={<Navigate to="/not-found" replace />} />
      </Routes>
    </>
  )
}

export default AppRoutes
